import React, { useState, useEffect } from "react";

// region Imports - External libraries
import {
  Button, TextField, CircularProgress, Typography, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from "@material-ui/core";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
// endregion Imports - External libraries
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { VehiclesModuleMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion - Services
// region Imports - Shared
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { HardwareTypesID } from "@shared/constants/hardware-types.enum";
import { StandardReturnObject } from "@shared/utils/schemas/standard-return-object";
import { HardwareLastCommunicationDto } from "@shared/dtos/hardwares/hardware-last-communication.dto";
import {
  HardwareWaterFactorVersionCalculationResponseDto
} from "@shared/dtos/hardware-water-factor-versions/hardware-water-factor-version-calculation-response.dto";
// endregion Imports - Shared
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles

interface WaterFactorControlTabProps {
  vehicle: Vehicle;
  onClose: () => void;
}
type AnalysisState = "idle" | "running" | "stopped" | "completed";

const WaterFactorControlTab: React.FC<WaterFactorControlTabProps> = ({ vehicle, onClose }) => {

  const { t } = useTranslation();
  const { addToast } = useToast();

  // region States
  const [analysisState, setAnalysisState] = useState<AnalysisState>("idle");
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [stopTime, setStopTime] = useState<Date | null>(null);
  const [timer, setTimer] = useState("00:00:00");
  const [userReportedWater, setUserReportedWater] = useState("");
  const [loading, setLoading] = useState(false);
  const [calculationResult, setCalculationResult] = useState<HardwareWaterFactorVersionCalculationResponseDto | null>(null);
  // endregion States
  // region Constants
  const activeHardwares = vehicle.hardwares?.filter(h => h.active) || [];
  const formatTime = (totalSeconds: number) => {
    const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, "0");
    const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, "0");
    const seconds = (totalSeconds % 60).toString().padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  };
  // endregion Constants
  // region Functions

  /**
   * Toggles the state of an analysis process between "idle", "running", and "stopped".
   * - When the analysis state is "idle", it sets the start time and changes the state to "running".
   *   PS: To start an analysis, the hardware must have been communicating with the server for at least 30 minutes.
   * - When the analysis state is "running", it sets the stop time and changes the state to "stopped".
   *
   * This function manages the control flow of the analysis process by tracking
   * the current state and appropriately updating related state variables.
   */
  const handleStartStop = async () => {
    if (analysisState === "idle") {
      const hardwareToCalculate = activeHardwares.find(h => h.type.id_hardware_type === HardwareTypesID.RASTREADOR);

      if (!hardwareToCalculate) return;

      try {
        const { data } = await api.get<StandardReturnObject<HardwareLastCommunicationDto>>(
          `/hardwares/last-communication/${hardwareToCalculate.id_hardware}`
        );

        if (data.status === "success" && data.result?.last_communication) {
          const lastCommunication = new Date(data.result.last_communication);
          const now = new Date();
          const diffInMinutes = (now.getTime() - lastCommunication.getTime()) / (1000 * 60);

          if (diffInMinutes > 30) {
            addToast({
              type: "info",
              title: t(ToastMessages.alert),
              description: t(VehiclesModuleMessages.controlTabHardwareOffline)
            });

            return;
          }
        } else if (data.status === "alert") {
          addToast({
            type: "info",
            title: t(ToastMessages.alert),
            description: data.message
          });

          return;
        }

        setStartTime(new Date());
        setAnalysisState("running");

      } catch (error) {
        if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
        else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
      }
    } else if (analysisState === "running") {
      setStopTime(new Date());
      setAnalysisState("stopped");
    }
  };

  /**
   * Asynchronously calculates water factors for a specific hardware type (RASTREADOR) based on user-provided inputs
   * and a specified time range. Handles API interaction, error handling, and result notifications.
   *
   * Functionality:
   * - Validates necessary inputs such as start time, stop time, user-reported water amount, and active hardware list.
   * - Sends a POST request to calculate water factors using specified hardware data.
   * - Displays appropriate toast notifications for success, informational messages, or errors.
   *
   * Pre-conditions:
   * - `hardwareToCalculate` must correspond to hardware of type "RASTREADOR".
   * - `startTime`, `stopTime`, `userReportedWater`, and `activeHardwares` must be defined and valid.
   *
   * Post-conditions:
   * - Displays a success notification if the calculation is successful.
   * - Displays an informational or error notification based on the API response or network errors.
   * - Resets the loading state upon completion.
   *
   * @throws Will handle and display relevant error messages using toast notifications if the operation fails.
   */
  const handleCalculate = async () => {
    const hardwareToCalculate = activeHardwares.find(h => h.type.id_hardware_type === HardwareTypesID.RASTREADOR);

    if (!startTime || !stopTime || !userReportedWater || activeHardwares.length === 0 || !hardwareToCalculate) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        startTime: startTime.toISOString(),
        stopTime: stopTime.toISOString(),
        userReportedWaterAmount: Number(userReportedWater)
      };

      const { data } = await api.post<StandardReturnObject<HardwareWaterFactorVersionCalculationResponseDto>>(
        `/hardwares/water-factor-versions/calculate/${hardwareToCalculate.id_hardware}`, payload
      );

      if (data.status === "success") {
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });
        setCalculationResult(data.result || null);
        setAnalysisState("completed");
      } else {
        addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });
      }
    } catch (error) {
      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resets the application's analysis state and timer to their default values.
   *
   * This function performs the following actions:
   * - Sets the analysis state to "idle".
   * - Clears the start time by setting it to null.
   * - Clears the stop time by setting it to null.
   * - Resets the timer display to "00:00:00".
   */
  const handleReset = () => {
    setAnalysisState("idle");
    setStartTime(null);
    setStopTime(null);
    setTimer("00:00:00");
    setUserReportedWater("");
    setCalculationResult(null);
  };

  // endregion Functions
  // region Effects

  // Updates the timer every second while the analysis is running
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (analysisState === "running" && startTime) {
      interval = setInterval(() => {
        const elapsedSeconds = Math.floor((new Date().getTime() - startTime.getTime()) / 1000);

        setTimer(formatTime(elapsedSeconds));
      }, 1000);
    }

    return () => clearInterval(interval);
  }, [analysisState, startTime]);

  // endregion Effects

  if (analysisState === "completed" && calculationResult) {
    return (
      <Container>
        <Box className="result-container">
          <Typography variant="h6">{t(VehiclesModuleMessages.waterFactorResultTitle)}</Typography>
          <Typography variant="h2" className="result-factor">
            {calculationResult.new_factor.toFixed(2)}
          </Typography>
          <Typography variant="body1" className="result-details">
            {t(VehiclesModuleMessages.waterFactorResultPreviousFactor)}: {calculationResult.previous_factor?.toFixed(2) || "N/A"}
          </Typography>
          <Box mt={2} className="result-details">
            <Typography variant="body2">
              {t(VehiclesModuleMessages.controlTabAnalysisPeriod)}: {new Date(calculationResult.analysis_start_time).toLocaleTimeString()}
              <ArrowForwardIcon fontSize="small" />
              {new Date(calculationResult.analysis_stop_time).toLocaleTimeString()}
            </Typography>
            <Typography variant="body2">
              {t(VehiclesModuleMessages.waterFactorResultHardwareWater)}: {calculationResult.water_difference.toFixed(2)} L
            </Typography>
            <Typography variant="body2">
              {t(VehiclesModuleMessages.waterFactorResultUserWater)}: {Number(userReportedWater).toFixed(2)} L
            </Typography>
          </Box>
        </Box>
        <div className="actions-container">
          <Button variant="outlined" onClick={handleReset}>
            {t(VehiclesModuleMessages.waterFactorResultNewCalibration)}
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Box className="info-box">
        <Typography variant="body2">
          {t(VehiclesModuleMessages.controlTabExplanatoryText)}:
        </Typography>
      </Box>
      <TableContainer component={Paper} className="hardware-table">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t(VehiclesModuleMessages.controlTabHardwareBrand)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.controlTabHardwareModel)}</TableCell>
              <TableCell>{t(VehiclesModuleMessages.controlTabHardwareDevId)}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activeHardwares.map((hw) => (
              <TableRow key={hw.id_hardware}>
                <TableCell>{hw.brand}</TableCell>
                <TableCell>{hw.model}</TableCell>
                <TableCell>{hw.dev_id}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="timer-container">
        <div className="timer">{timer}</div>
        <div className="analysis-period">
          <Typography variant="caption">{t(VehiclesModuleMessages.controlTabAnalysisPeriod)}</Typography>
          <div className="analysis-times">
            <span>{startTime ? startTime.toLocaleTimeString() : "--:--:--"}</span>
            <ArrowForwardIcon fontSize="small" />
            <span>{stopTime ? stopTime.toLocaleTimeString() : "--:--:--"}</span>
          </div>
        </div>
      </div>
      <div className="actions-container">
        <Button
          variant="contained"
          color={analysisState === "idle" ? "primary" : "secondary"}
          onClick={handleStartStop}
          disabled={analysisState === "stopped"}
        >
          {analysisState === "idle" && t(VehiclesModuleMessages.controlTabStartAnalysis)}
          {analysisState === "running" && t(VehiclesModuleMessages.controlTabStopAnalysis)}
          {analysisState === "stopped" && "-"}
        </Button>
        {analysisState === "stopped" && (
          <Button variant="outlined" onClick={handleReset}>
            {t(VehiclesModuleMessages.controlTabResetAnalysis)}
          </Button>
        )}
      </div>
      <div className="form-container">
        <TextField
          label={t(VehiclesModuleMessages.controlTabAmountOfWater)}
          variant="outlined"
          type="number"
          value={userReportedWater}
          onChange={(e) => setUserReportedWater(e.target.value)}
          inputProps={{ min: 1, max: 1000 }}
          fullWidth
        />
        <Button
          variant="contained"
          color="primary"
          onClick={handleCalculate}
          disabled={analysisState !== "stopped" || !userReportedWater || loading}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {t(VehiclesModuleMessages.controlTabCalculateButton)}
        </Button>
      </div>
    </Container>
  );
};

export default WaterFactorControlTab;
